import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { InputBarConversationController } from "@/components/panels/input-bar-types";
import { useInputBarTarget } from "@/stores/input-bar-target-store";

export type ConversationFrameView = "current" | "new-pending";

export function useConversationFrameRouting(
  conversationId: string | undefined,
  executionId: string | undefined,
  canStartNewConversation = true,
  frameId?: string,
): {
  conversationController: InputBarConversationController;
  view: ConversationFrameView;
} {
  const pendingConversationIdRef = useRef<string | undefined>(undefined);
  const pendingExecutionIdRef = useRef<string | undefined>(undefined);
  const externalIntentIdRef = useRef<string | null>(null);
  const [view, setView] = useState<ConversationFrameView>("current");
  const externalIntentId = useInputBarTarget((state) =>
    frameId &&
    state.startNewConversation &&
    state.newConversationFrameId === frameId
      ? state.intentId
      : null,
  );

  useEffect(() => {
    if (
      view === "new-pending" &&
      conversationId !== undefined &&
      (pendingConversationIdRef.current !== undefined
        ? pendingConversationIdRef.current !== conversationId
        : executionId !== undefined &&
          pendingExecutionIdRef.current !== executionId)
    ) {
      pendingConversationIdRef.current = undefined;
      pendingExecutionIdRef.current = undefined;
      setView("current");
    }
  }, [conversationId, executionId, view]);

  useEffect(() => {
    if (externalIntentId) {
      if (externalIntentIdRef.current === externalIntentId) return;
      externalIntentIdRef.current = externalIntentId;
      pendingConversationIdRef.current = conversationId;
      pendingExecutionIdRef.current = executionId;
      setView("new-pending");
      return;
    }
    if (!externalIntentIdRef.current) return;
    const conversationChanged =
      pendingConversationIdRef.current !== undefined
        ? pendingConversationIdRef.current !== conversationId
        : pendingExecutionIdRef.current !== undefined &&
          pendingExecutionIdRef.current !== executionId;
    externalIntentIdRef.current = null;
    if (!conversationChanged) {
      pendingConversationIdRef.current = undefined;
      pendingExecutionIdRef.current = undefined;
      setView("current");
    }
  }, [conversationId, executionId, externalIntentId]);

  const onClear = useCallback(() => {
    pendingConversationIdRef.current = undefined;
    pendingExecutionIdRef.current = undefined;
    externalIntentIdRef.current = null;
    setView("current");
  }, []);
  const onNewConversation = useCallback(() => {
    if (!canStartNewConversation) return;
    pendingConversationIdRef.current = conversationId;
    pendingExecutionIdRef.current = executionId;
    externalIntentIdRef.current = null;
    setView("new-pending");
  }, [canStartNewConversation, conversationId, executionId]);
  const conversationController = useMemo<InputBarConversationController>(
    () => ({
      conversationId,
      target: null,
      startNewConversation: view === "new-pending",
      canStartNewConversation,
      onClear,
      onNewConversation,
    }),
    [canStartNewConversation, conversationId, onClear, onNewConversation, view],
  );

  return { conversationController, view };
}
